import { gql } from "@apollo/client";
import type { SyncableVmp } from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { Vmp as RxVmp } from "../collections/vmp";
import type { RxDBCollections } from "../provider";

import {
  SyncVmpPullDocument,
  type WholeSyncableVmpFragment,
} from "./__generated__/vmp.gql";
import { useGqlReplication } from "./use-gql-replication";

export const useReplicateVmp = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<RxVmp>;
}) => {
  const pull = useMemo(
    () => ({
      query: SyncVmpPullDocument,
      buildInputVariables: async (checkpoint, batchSize) => ({
        input: {
          limit: batchSize,
          checkpoint,
        },
      }),
      buildPullCheckpoint: async (doc) => ({
        id: doc.id,
        updatedAt: doc.meta.updatedAt,
      }),
      reSyncInterval: { hours: 1 },
    }),
    [],
  );

  return useGqlReplication<RxVmp, SyncableVmp, null>({
    db,
    collection,
    pull,
    mapGqlToRx: mapGqlVmpToRxVmp,
    batchSize: 1000,
    metadataLocalStorageKeySuffix: "vmp",
  });
};

export const mapGqlVmpToRxVmp = (
  vmp: WholeSyncableVmpFragment,
): WithDeleted<RxVmp> => ({
  id: vmp.id,
  title: vmp.title,
  keywords: [...vmp.keywords],
  snomedId: vmp.snomedId ?? null,
  categoryId: vmp.categoryId ?? null,
  formId: vmp.formId,
  legacyMedicationId: vmp.legacyMedicationId,
  vtmId: vmp.vtmId,
  productType: vmp.productType,
  meta: {
    createdAt: vmp.meta.createdAt,
    createdByUserId: vmp.meta.createdByUserId,
    updatedAt: vmp.meta.updatedAt,
    updatedByUserId: vmp.meta.updatedByUserId,
  },
  _deleted: vmp.meta.deleted,
});

gql`
  fragment WholeSyncableVmp on SyncableVmp {
    id
    title
    keywords
    snomedId
    formId
    categoryId
    legacyMedicationId
    productType
    vtmId
    meta {
      createdAt
      createdByUserId
      updatedAt
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncVmpPull($input: PullVmpInput!) {
    pullVmp(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncableVmp
      }
    }
  }
`;
